import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMemberSubscription,
  useQueryMemberCalculateProRataForChangingSubscription,
  useQueryMemberPaymentMethodListNew,
} from "@gymflow/api";
import { formatCurrency } from "@gymflow/helpers";
import CustomTooltip from "apps/portal/src/components/atoms/CustomTooltip";
import useScaModal from "apps/portal/src/hooks/useScaModal";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { ApiResolverContext } from "../../../../../providers/ApiResolverProvider/ApiResolverContext";
import { ToastContext } from "../../../../../providers/ToastProvider/context";
import {
  FormCheckboxInput,
  FormLabeledSwitch,
  InfoBlock,
  WarningBlock,
  WarningSection,
} from "../../../../atoms";
import { ConfirmModal } from "../../../../templates";
import { DescriptionBlock } from "./components/DescriptionBlock";
import { FormNewMembershipSelect } from "./components/FormNewMembershipSelect";
import { FormScheduledDateSelect } from "./components/FormScheduledDateSelect";
import {
  changeMembershipFormSchema,
  INITIAL_VALUES_CHANGE_MEMBERSHIP_FORM,
} from "./constants";
import {
  ChangeMembershipFormValues,
  ChangeMembershipModalProps,
} from "./types";

export const ChangeMembershipModal = ({
  memberId,
  activeMembership,
  linkedMembers,
  hide,
}: ChangeMembershipModalProps) => {
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { api } = useGymflowModels();
  const showScaModal = useScaModal({
    asMember: false,
  });
  const { toast } = useContext(ToastContext);
  const { data: paymentMethods } = useQueryMemberPaymentMethodListNew({
    api,
    clubId,
    memberId,
  });

  const {
    changeSubscriptionPaymentProRataMutation,
    changeSubscriptionPaymentWaivedMutation,
    changeSubscriptionScheduleMutation,
  } = useMemberSubscription({
    api,
    tz: club.timezone,
  });

  const subscriptionId = activeMembership.id;

  const formik = useFormik<ChangeMembershipFormValues>({
    initialValues: INITIAL_VALUES_CHANGE_MEMBERSHIP_FORM,
    validationSchema: changeMembershipFormSchema,
    onSubmit: async ({
      newMembership,
      isChangeImmediately,
      isCollectUpgradeCost,
      date,
    }) => {
      const defaultPaymentMethod = paymentMethods?.find(
        (pm) => pm.defaultPaymentMethod,
      );

      if (!defaultPaymentMethod) {
        toast({
          message: t(
            "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.toast.notFoundDefaultPayment",
          ),
          intent: "error",
        });
        return;
      }

      const newMembershipId = newMembership?.value.id;

      if (!newMembershipId) return;

      if (isChangeImmediately) {
        // FYI: in case if subscription downgraded -> it's not necessary send request from pro-rata or waived
        if (isCollectUpgradeCost) {
          const { data } =
            await changeSubscriptionPaymentProRataMutation.mutateAsync({
              subscriptionId,
              newMembershipId,
              memberId,
            });
          await showScaModal({
            invoiceNumber: data?.invoiceNumber,
            userMemberId: memberId,
          });
        } else {
          await changeSubscriptionPaymentWaivedMutation.mutateAsync({
            subscriptionId,
            newMembershipId,
            memberId,
          });
        }
      } else if (!isChangeImmediately && date) {
        await changeSubscriptionScheduleMutation.mutateAsync({
          subscriptionId,
          newMembershipId,
          changeDate: date.value,
          memberId,
        });
      }
      hide();
    },
  });

  const {
    values: { isChangeImmediately, newMembership },
  } = formik;

  const newMembershipId = newMembership?.value.id;

  const { data: calculateProRataData } =
    useQueryMemberCalculateProRataForChangingSubscription(
      {
        api,
        membershipId: newMembershipId ?? 0,
        subscriptionId,
      },
      { enabled: !!newMembershipId && !!subscriptionId && isChangeImmediately },
    );

  const prorata = calculateProRataData?.price;

  const isProRataForChangingSubscription =
    prorata !== null && prorata !== undefined && prorata > 0;
  const [parent] = useAutoAnimate();
  return (
    <ConfirmModal
      title={t(
        "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.title",
      )}
      showCloseButton
      onHide={() => hide()}
      onConfirm={async () => await formik.submitForm()}
      onCancel={() => hide()}
    >
      <div ref={parent} className="flex flex-col gap-4">
        <DescriptionBlock isChangeImmediately={isChangeImmediately} />
        {subscriptionId !== activeMembership.primarySubscriptionId && (
          <WarningBlock
            text={t(
              "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.warningBlock.partOfShared",
            )}
          />
        )}
        {subscriptionId === activeMembership.primarySubscriptionId &&
          linkedMembers &&
          linkedMembers?.length > 1 && (
            <InfoBlock
              text={t(
                "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.warningBlock.removedForAllLinkedMembers",
              )}
            />
          )}
        <FormikProvider value={formik}>
          <div className="flex flex-col gap-2">
            <FormNewMembershipSelect activeMembership={activeMembership} />
            {isProRataForChangingSubscription && isChangeImmediately && (
              <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
                <FormCheckboxInput<ChangeMembershipFormValues>
                  name="isCollectUpgradeCost"
                  label={t(
                    "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.checkbox.label",
                  )}
                />
                <div className="flex flex-row items-center gap-4">
                  <WarningBlock
                    className="flex-1 items-center"
                    text={t(
                      "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.warningBlock.upgradeCost",
                      {
                        upgradeCost: formatCurrency(
                          prorata,
                          club.defaultCurrency,
                        ),
                      },
                    )}
                  />
                  <CustomTooltip
                    message={t(
                      "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.warningBlock.upgradeCostTooltip",
                      {
                        currentSubscriptionValue: formatCurrency(
                          calculateProRataData?.currentSubscriptionValue || 0,
                          club.defaultCurrency,
                        ),
                        newMembershipPrice: formatCurrency(
                          calculateProRataData?.newMembershipPrice || 0,
                          club.defaultCurrency,
                        ),
                      },
                    )}
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className="mr-2 size-4 text-gray-500"
                    />
                  </CustomTooltip>
                </div>
              </div>
            )}
          </div>
          <FormLabeledSwitch<ChangeMembershipFormValues>
            name="isChangeImmediately"
            label={t(
              "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.switch.label",
            )}
            className="-ml-1 -mr-2 scale-75"
          />
          {!isChangeImmediately && (
            <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
              <FormScheduledDateSelect activeMembership={activeMembership} />
              <InfoBlock
                text={t(
                  "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.infoBlock.billingPeriod",
                )}
              />
            </div>
          )}
        </FormikProvider>
        {isChangeImmediately &&
          newMembership &&
          newMembership.value.billingType !== activeMembership.billingType && (
            <WarningSection
              label={t(
                "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.billingDateChangeWarning.title",
              )}
              subLabel={t(
                "page.userMember.tab.userMemberAccount.userMemberMembership.membershipCard.modal.changeMembershipModal.billingDateChangeWarning.description",
              )}
            />
          )}
      </div>
    </ConfirmModal>
  );
};
